// AudioPlaylistPlayer.tsx
import * as React from "react";
import {
    IconButton,
    Slider,
    Menu,
    MenuItem,
    Typography,
    CircularProgress,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import Replay10RoundedIcon from "@mui/icons-material/Replay10Rounded";
import Forward10RoundedIcon from "@mui/icons-material/Forward10Rounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { colors } from "../../../../../constants/colors";
import clsx from "clsx";

type AudioItem = {
    /** ชื่อแทร็ก (เช่น บทนำ, EP 1 …) */
    title: string;
    /** ไฟล์เสียง */
    url: string;
    /** ความยาวเป็นวินาที (ถ้าไม่รู้ ปล่อยว่าง เดี๋ยวอ่านจาก metadata) */
    durationSec?: number;
    /** วันที่เพิ่ม ISO string หรือรูปแบบอื่นที่ parse ได้ */
    disabled_item?: boolean;
    date?: string;
    is_example?: boolean;
};

type Props = {
    /** Array รายการเสียง — index 0 = บทนำ/ตอนแรก */
    items: AudioItem[];
    /** ค่าเริ่มต้น index (ถ้าไม่ได้ระบุ จะเลือกตัวแรกที่เป็น is_example) */
    initialIndex?: number;
    /** สีกล่อง player (tailwind class) */
    playerBgClass?: string;
    /** callback เมื่อเปลี่ยนรายการ */
    onEpisodeChange?: (index: number, item: AudioItem) => void;
    /** ค่าข้าม (วินาที) เวลาแตะย้อน/ไปข้างหน้า */
    seekStepSec?: number;
    fullWidth?: boolean;
};

const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, n));

const fmtTime = (sec?: number) => {
    if (sec == null || !isFinite(sec)) return "-00:00";
    const s = Math.floor(sec % 60)
        .toString()
        .padStart(2, "0");
    const m = Math.floor((sec / 60) % 60)
        .toString()
        .padStart(2, "0");
    const h = Math.floor(sec / 3600);
    return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
};

const fmtDate = (d?: string) => {
    if (!d) return "";
    try {
        const dt = new Date(d);
        return dt.toLocaleDateString("th-TH", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    } catch {
        return d;
    }
};

const speeds = [0.75, 1, 1.25, 1.5, 1.75, 2] as const;

const AudioPlaylistPlayer: React.FC<Props> = ({
    items,
    initialIndex,
    playerBgClass = "bg-white",
    onEpisodeChange,
    fullWidth,
    seekStepSec = 15,
}) => {
    // เลือก index เริ่มต้น: ถ้ามี is_example ให้เลือกตัวแรก, ถ้าไม่มีค่อยใช้ initialIndex/0
    const computedInitialIndex = React.useMemo(() => {
        const exIdx = items.findIndex((it) => it.is_example);
        const fallback = initialIndex ?? 0;
        const raw = exIdx >= 0 ? exIdx : fallback;
        return clamp(raw, 0, Math.max(0, items.length - 1));
    }, [items, initialIndex]);

    const [index, setIndex] = React.useState<number>(computedInitialIndex);
    const [playing, setPlaying] = React.useState(false);
    const [rate, setRate] = React.useState<number>(1);
    const [current, setCurrent] = React.useState(0);
    const [duration, setDuration] = React.useState<number | undefined>(
        items[computedInitialIndex]?.durationSec
    );
    const [buffering, setBuffering] = React.useState(false);
    const [sortDesc, setSortDesc] = React.useState(true);

    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const progressPct = duration ? (current / duration) * 100 : 0;

    const currentItem = items[index];

    // ถ้า items เปลี่ยนจน index เกินขอบ หรือเปลี่ยนชุดรายการ ให้รีคอมพิวต์ index ตามกติกาเดิม
    React.useEffect(() => {
        setIndex((prev) => {
            if (items.length === 0) return 0;
            if (prev < items.length) return prev;
            return computedInitialIndex;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // เปลี่ยนตอน: โหลดแต่ "ไม่ autoplay"
    React.useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        setCurrent(0);
        setDuration(currentItem?.durationSec);
        setBuffering(true);

        a.src = currentItem?.url ?? "";
        a.playbackRate = rate;
        a.load(); // โหลด metadata/ไฟล์แต่ไม่เล่น
        setPlaying(false);
        onEpisodeChange?.(index, currentItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    // เปลี่ยนความเร็ว (ไม่มีผลถ้าไม่ได้เล่น)
    React.useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = rate;
    }, [rate]);

    // event listeners กับ audio element
    React.useEffect(() => {
        const a = audioRef.current;
        if (!a) return;

        const onTime = () => setCurrent(a.currentTime);
        const onLoaded = () => {
            setDuration(Number.isFinite(a.duration) ? a.duration : undefined);
            setBuffering(false);
        };
        // waiting/playing มักจะเกี่ยวกับระหว่างเล่น แต่ส่งไว้เผื่อกดเล่น
        const onWaiting = () => setBuffering(true);
        const onPlaying = () => setBuffering(false);
        const onEnded = () => {
            // ไม่ autoplay ตอนถัดไป: แค่สลับตอน แต่ยัง "หยุด" ไว้
            setIndex((i) => clamp(i + 1, 0, Math.max(0, items.length - 1)));
            setPlaying(false);
        };

        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onLoaded);
        a.addEventListener("waiting", onWaiting);
        a.addEventListener("playing", onPlaying);
        a.addEventListener("ended", onEnded);

        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onLoaded);
            a.removeEventListener("waiting", onWaiting);
            a.removeEventListener("playing", onPlaying);
            a.removeEventListener("ended", onEnded);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index, items.length]);

    const togglePlay = async () => {
        const a = audioRef.current;
        if (!a) return;
        if (playing) {
            a.pause();
            setPlaying(false);
        } else {
            try {
                await a.play();
                setPlaying(true);
            } catch {
                setPlaying(false);
            }
        }
    };

    const handleSeek = (_: Event, v: number | number[]) => {
        if (!audioRef.current || duration == null) return;
        const pct = Array.isArray(v) ? v[0] : v;
        const t = (pct / 100) * duration;
        audioRef.current.currentTime = t;
        setCurrent(t);
    };
    const handlePrev = () =>
        setIndex((i) => clamp(i - 1, 0, Math.max(0, items.length - 1)));
    const handleNext = () =>
        setIndex((i) => clamp(i + 1, 0, Math.max(0, items.length - 1)));

    const handleJump = (sec: number) => {
        const a = audioRef.current;
        if (!a || duration == null) return;
        const t = clamp(a.currentTime + sec, 0, duration);
        a.currentTime = t;
        setCurrent(t);
    };

    const [speedEl, setSpeedEl] = React.useState<null | HTMLElement>(null);
    const openSpeed = Boolean(speedEl);

    // เรียงลิสต์: is_example ขึ้นก่อนเสมอ แล้วค่อยเรียงตามวัน (ใหม่สุด/เก่าสุด)
    const playlist = React.useMemo(() => {
        const withIdx = items.map((it, i) => ({ ...it, _i: i }));
        withIdx.sort((a, b) => {
            // is_example ก่อน
            if (a.is_example && !b.is_example) return -1;
            if (!a.is_example && b.is_example) return 1;

            const ta = a.date ? new Date(a.date).getTime() : 0;
            const tb = b.date ? new Date(b.date).getTime() : 0;
            return sortDesc ? tb - ta : ta - tb;
        });
        return withIdx;
    }, [items, sortDesc]);

    return (
        <div className={fullWidth ? "w-full" : "w-full max-w-[920px] mx-auto"}>
            <div className={`w-full ${playerBgClass} rounded-3xl shadow-md p-5 md:p-6 mb-6`}>
                <div className="flex items-center justify-between mb-2">
                    <div />
                    <Typography textAlign={"center"} variant="h4" className="font-semibold line-clamp-1">
                        {currentItem?.title ?? "—"}
                    </Typography>

                    <button
                        className="text-sm px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200"
                        onClick={(e) => setSpeedEl(e.currentTarget as HTMLElement)}
                        aria-haspopup="menu"
                    >
                        {rate}x
                    </button>
                    <Menu open={openSpeed} anchorEl={speedEl} onClose={() => setSpeedEl(null)}>
                        {speeds.map((s) => (
                            <MenuItem
                                key={s}
                                selected={rate === s}
                                onClick={() => {
                                    setRate(s);
                                    setSpeedEl(null);
                                }}
                            >
                                {s}x
                            </MenuItem>
                        ))}
                    </Menu>
                </div>

                {/* Timeline */}
                <div className="flex items-center gap-3">
                    <span className="text-sm tabular-nums">{fmtTime(current)}</span>
                    <Slider
                        value={progressPct}
                        onChange={handleSeek}
                        aria-label="progress"
                        sx={{ color: colors.themeMainColorTwo }}
                        className="flex-1"
                    />
                    <span className="text-sm tabular-nums">
                        -{fmtTime((duration ?? 0) - current)}
                    </span>
                </div>

                {/* Controls */}
                <div className="mt-2 flex items-center justify-center gap-1 md:gap-3">
                    <IconButton aria-label="previous" onClick={handlePrev} disabled={index === 0}>
                        <SkipPreviousRoundedIcon />
                    </IconButton>
                    <IconButton aria-label={`ย้อน ${seekStepSec} วิ`} onClick={() => handleJump(-seekStepSec)}>
                        <Replay10RoundedIcon />
                    </IconButton>
                    <IconButton aria-label={playing ? "pause" : "play"} onClick={togglePlay} className="!w-14 !h-14">
                        {playing ? <PauseRoundedIcon fontSize="large" /> : <PlayArrowRoundedIcon fontSize="large" />}
                    </IconButton>
                    <IconButton aria-label={`ข้าม ${seekStepSec} วิ`} onClick={() => handleJump(seekStepSec)}>
                        <Forward10RoundedIcon />
                    </IconButton>
                    <IconButton aria-label="next" onClick={handleNext} disabled={index >= items.length - 1}>
                        <SkipNextRoundedIcon />
                    </IconButton>

                    {buffering && <CircularProgress size={20} className="ml-2" />}
                    <IconButton className="ml-auto">
                        <MoreHorizRoundedIcon />
                    </IconButton>
                </div>

                {/* hidden audio element */}
                <audio ref={audioRef} preload="metadata" />
            </div>

            {/* LIST HEADER */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-baseline gap-6">
                    <Typography variant="h5" className="font-semibold">
                        ตอน
                    </Typography>
                    <Typography variant="h5" className="text-gray-500 text-sm">
                        จำนวน : {items.length} EP.
                    </Typography>
                </div>

                <button
                    className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                    onClick={() => setSortDesc((s) => !s)}
                    title="สลับเรียงตามวันที่"
                >
                    วันที่เพิ่ม ({sortDesc ? "ใหม่สุด" : "เก่าสุด"})
                    <svg
                        className={`w-4 h-4 transition ${sortDesc ? "" : "rotate-180"}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                    </svg>
                </button>
            </div>

            {/* PLAYLIST */}
            <div className="divide-y">
                {playlist.map((it) => {
                    const active = it._i === index;
                    return (
                        <button
                            disabled={it.disabled_item}
                            key={`${it.url}-${it._i}`}
                            className={clsx(
                                "w-full py-3 px-2 text-left hover:bg-gray-50 focus:bg-gray-50 rounded-lg flex items-center gap-3",
                                { "text-[#BBBCBE]": it.disabled_item }
                            )}
                            onClick={() => (it.disabled_item ? undefined : setIndex(it._i))}
                        >
                            <div className="flex-1">
                                <div className={`text-sm md:text-base ${active ? "font-semibold" : "font-normal"}`}>
                                    {it.title}
                                </div>
                                <div
                                    className={clsx("text-xs ", {
                                        "text-[#BBBCBE]": it.disabled_item,
                                        "text-gray-500": !it.disabled_item,
                                    })}
                                >
                                    {fmtDate(it.date)}
                                </div>
                            </div>

                            <div className="text-sm text-gray-600 tabular-nums">{fmtTime(it.durationSec)}</div>
                            <div
                                className={`w-6 h-6 rounded-full grid place-items-center ${active ? "bg-black text-white" : "bg-gray-100 text-gray-700"
                                    }`}
                                aria-label={active ? (playing ? "กำลังเล่น" : "เลือกอยู่") : "เล่นตอนนี้"}
                            >
                                {active ? (playing ? <PauseRoundedIcon fontSize="small" /> : <PlayArrowRoundedIcon fontSize="small" />) : <PlayArrowRoundedIcon fontSize="small" />}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AudioPlaylistPlayer;
